<template>
  <div class="w-full">
    <div
      class="flex flex-col sm:flex-row items-start sm:items-center
        justify-center gap-3 flex-wrap"
    >
      <span class="text-base sm:text-lg font-medium text-gray-700">
        {{ $t('MyResultsDashboardComponent.compareWithPastOrTeam') }}
      </span>

      <Select
        v-model="localComparison1"
        :options="comparisonOptions"
        :optionLabel="optionLabel"
        optionValue="value"
        optionGroupLabel="label"
        optionGroupChildren="items"
        :placeholder="$t('CompareOimComponent.compareTo')"
        showClear
        class="w-fit max-w-full min-w-0"
        @change="onComparisonChange"
      />

      <span class="text-base sm:text-lg font-medium text-gray-700">
        {{ $t('MyResultsDashboardComponent.compareTo') }}
      </span>

      <Select
        v-model="localComparison2"
        :options="comparisonOptions"
        :optionLabel="optionLabel"
        optionValue="value"
        optionGroupLabel="label"
        optionGroupChildren="items"
        :placeholder="$t('CompareOimComponent.compareTo')"
        showClear
        class="w-fit max-w-full min-w-0"
        @change="onComparisonChange"
      />

      <button
        @click="onReset"
        class="px-4 py-2 text-sm sm:text-base font-medium text-white bg-gray-600 rounded-md
          hover:bg-gray-700 transition-colors"
      >
        {{ $t('MyResultsDashboardComponent.reset') }}
      </button>
    </div>

    <!-- Delta badges (shown when comparing two assessments) -->
    <div
      v-if="deltas && deltas.length > 0"
      class="mt-4 flex flex-wrap gap-2 justify-center"
    >
      <div
        v-for="delta in deltas"
        :key="delta.key"
        class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm sm:text-base font-medium bg-gray-50"
        :class="deltaClass(delta)"
      >
        <i
          :class="deltaIconClass(delta.value)"
          class="text-xs"
        ></i>
        <span class="truncate max-w-[80px]" :title="delta.label">{{ delta.label }}</span>
        <span class="font-semibold">{{ formatDelta(delta.value) }}</span>
      </div>
    </div>
    <p
      v-if="deltaSummary"
      class="text-sm sm:text-base text-gray-600 text-center mt-2"
    >
      {{ deltaSummary }}
    </p>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Select from 'primevue/select';
import { formatLocaleDate } from '@/helpers/formatLocaleDate';
import { getFeatureDisplayNameOrFallback } from '@/utils/featureDisplay';

const props = defineProps({
  /** Assessment ID of the current/latest assessment */
  currentAssessmentId: { type: String, default: '' },
  /** Current comparison1 value */
  comparison1: { type: String, default: '' },
  /** Current comparison2 value */
  comparison2: { type: String, default: '' },
  /** Array of same-type assessments: [{ _id, assessmentName, date_assessmentMade }] */
  sameTypeAssessments: { type: Array, default: () => [] },
  /** Array of external evaluation assessments: [{ _id, assessmentName, date_assessmentMade }] */
  externalEvaluations: { type: Array, default: () => [] },
  /** When true, same-type list is external-evaluation submissions only (label i18n). */
  isExternalEvaluationTab: { type: Boolean, default: false },
  /** Computed deltas: [{ key, label, value }] when two assessments are selected */
  deltas: { type: Array, default: null },
  /** Helper to get assessment display name */
  getAssessmentName: { type: Function, default: (name) => name },
});

const emit = defineEmits(['update:comparison1', 'update:comparison2', 'reset', 'comparison-change']);

const { t, locale } = useI18n();
const store = useStore();

const localComparison1 = ref(props.comparison1);
const localComparison2 = ref(props.comparison2);

watch(() => props.comparison1, (v) => { localComparison1.value = v; });
watch(() => props.comparison2, (v) => { localComparison2.value = v; });

const featureName = computed(() => getFeatureDisplayNameOrFallback(
  store,
  'externalEvaluation',
  t('FallbackTexts.externalEvaluation'),
  locale.value,
));
const comparisonOptions = computed(() => {
  const groups = [];

  // Same type assessments
  if (props.sameTypeAssessments.length > 0) {
    const sameTypeLabel = props.isExternalEvaluationTab
      ? (t('MyResultsDashboardComponent.externalEvaluationComparisonGroup', { text: featureName.value }) || 'External evaluation')
      : (t('MyResultsDashboardComponent.selfAssessment') || 'Self Assessment');
    groups.push({
      label: sameTypeLabel,
      items: props.sameTypeAssessments.map((a) => ({
        label: `${props.getAssessmentName(a.assessmentName)} - ${formatLocaleDate(a.date_assessmentMade, locale.value)}${a._id === props.currentAssessmentId ? ' (latest)' : ''}`,
        value: a._id,
      })),
    });
  }

  // External evaluations
  if (props.externalEvaluations.length > 0) {
    groups.push({
      label: t('MyResultsDashboardComponent.externalAssessment') || 'External Assessment',
      items: props.externalEvaluations.map((a) => ({
        label: `${props.getAssessmentName(a.assessmentName)} - ${formatLocaleDate(a.date_assessmentMade, locale.value)} (${t('MyResultsDashboardComponent.external') || 'External'})`,
        value: `external-${a._id}`,
      })),
    });
  }

  return groups;
});

function optionLabel(option) {
  return option?.label || '';
}

function onComparisonChange() {
  emit('update:comparison1', localComparison1.value || '');
  emit('update:comparison2', localComparison2.value || '');
  emit('comparison-change');
}

function onReset() {
  localComparison1.value = '';
  localComparison2.value = '';
  emit('update:comparison1', '');
  emit('update:comparison2', '');
  emit('reset');
}

const deltaSummary = computed(() => {
  if (!props.deltas || props.deltas.length === 0) return null;
  const improved = props.deltas.filter((d) => d.value > 0).length;
  const declined = props.deltas.filter((d) => d.value < 0).length;
  const total = props.deltas.length;
  return t('ResultsInsightsPanel.changeSummary', {
    improved,
    declined,
    total,
  });
});

function deltaIconClass(value) {
  if (value > 0) return 'pi pi-arrow-up';
  if (value < 0) return 'pi pi-arrow-down';
  return 'pi pi-minus';
}

/** Same font colors as change-over-time: positive / no change / negative */
function deltaClass(delta) {
  if (delta.value > 0) return 'text-[#e4821a]';
  if (delta.value < 0) return 'text-[#e44b55]';
  return 'text-[#475569]';
}

function formatDelta(value) {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}`;
}
</script>
